/**
 * SupportCaseDialog — user-facing "submit a support case" form.
 *
 * UI-only for now. The backend endpoint for creating support tickets
 * doesn't exist yet, so on submit we:
 *   1. Log the payload to the console (so it's visible in dev).
 *   2. Close the dialog and show a success snackbar.
 *   3. When the backend lands (e.g. POST /v1/support/cases), wire it
 *      up inside `handleSubmit` — the form shape here is intentionally
 *      aligned with what that endpoint will accept
 *      (category / subject / description / priority).
 *
 * Opening pattern — window event so any component anywhere in the tree
 * can request a case to be opened without prop drilling:
 *
 *     window.dispatchEvent(new CustomEvent('svdy:support:open', {
 *       detail: { category: 'organization-delete',
 *                 subject: 'Request to delete Alpha Logistics Co.' }
 *     }));
 *
 * Mount this component once (TopNav does), and it will listen for that
 * event and open itself with the supplied prefill.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  SupportAgentOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  SUPPORT_CATEGORIES,
  SUPPORT_PRIORITIES,
  supportCategoryLabel,
  supportPriorityLabel,
} from './supportDisplay';

// ---------------------------------------------------------------------------
// Public event contract
// ---------------------------------------------------------------------------

export const SUPPORT_OPEN_EVENT = 'svdy:support:open';

export interface SupportOpenDetail {
  category?: string;
  subject?: string;
  description?: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
}

/** Convenience helper — any component can call this to open the dialog. */
export function openSupportCase(detail: SupportOpenDetail = {}) {
  window.dispatchEvent(
    new CustomEvent<SupportOpenDetail>(SUPPORT_OPEN_EVENT, { detail }),
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SupportCaseDialog: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<string>('other');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] =
    useState<SupportOpenDetail['priority']>('normal');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // -------------------------------------------------------------------
  // Listen for window events.
  // -------------------------------------------------------------------
  useEffect(() => {
    const handler = (evt: Event) => {
      const e = evt as CustomEvent<SupportOpenDetail>;
      const detail = e.detail ?? {};
      setCategory(detail.category ?? 'other');
      setSubject(detail.subject ?? '');
      setDescription(detail.description ?? '');
      setPriority(detail.priority ?? 'normal');
      setErrors({});
      setOpen(true);
    };
    window.addEventListener(SUPPORT_OPEN_EVENT, handler);
    return () => window.removeEventListener(SUPPORT_OPEN_EVENT, handler);
  }, []);

  // -------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    const errs: Record<string, string> = {};
    if (!subject.trim()) errs.subject = t('common:support.subjectRequired');
    if (!description.trim())
      errs.description = t('common:support.descriptionRequired');
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const { default: platformApi } = await import('../../api/client');
      // Fetch backend version for bug context
      let backendCommit = '—';
      try {
        const ver = await platformApi.get('/v1/public/version');
        backendCommit = ver.data?.commit ?? '—';
      } catch {}
      const frontendCommit = process.env.REACT_APP_GIT_SHA ?? 'dev';

      await platformApi.post('/v1/support/cases', {
        category,
        priority,
        subject,
        description: `${description}\n\n---\nFrontend: ${frontendCommit}\nBackend: ${backendCommit}`,
      });
      setOpen(false);
      setToast(t('common:support.successToast'));
      setSubject('');
      setDescription('');
      setCategory('other');
      setPriority('normal');
    } catch (err: any) {
      setToast(err?.response?.data?.detail || t('common:support.failedToast'));
    } finally {
      setSubmitting(false);
    }
  }, [category, priority, subject, description, t]);

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !submitting && setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SupportAgentOutlined fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {category === 'feature' || category === 'bug'
                  ? t('common:support.feedbackTitle')
                  : t('common:support.dialogTitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {category === 'feature' || category === 'bug'
                  ? t('common:support.feedbackSubtitle')
                  : t('common:support.dialogSubtitle')}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
              >
                <FormControl fullWidth size="small">
                  <InputLabel id="case-category-label">{t('common:support.categoryLabel')}</InputLabel>
                  <Select
                    labelId="case-category-label"
                    label={t('common:support.categoryLabel')}
                    value={category}
                    renderValue={(value) => supportCategoryLabel(t, String(value))}
                    onChange={(e) => setCategory(String(e.target.value))}
                  >
                    {SUPPORT_CATEGORIES.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {t(c.i18nKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel id="case-priority-label">{t('common:support.priorityLabel')}</InputLabel>
                  <Select
                    labelId="case-priority-label"
                    label={t('common:support.priorityLabel')}
                    value={priority}
                    renderValue={(value) => supportPriorityLabel(t, String(value))}
                    onChange={(e) =>
                      setPriority(
                        e.target.value as SupportOpenDetail['priority'],
                      )
                    }
                  >
                    {SUPPORT_PRIORITIES.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {t(p.i18nKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <TextField
              label={t('common:support.subjectLabel')}
              placeholder={t('common:support.subjectPlaceholder')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              error={!!errors.subject}
              helperText={errors.subject}
              size="small"
              fullWidth
              autoFocus
            />

            <TextField
              label={t('common:support.descriptionLabel')}
              placeholder={t('common:support.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              error={!!errors.description}
              helperText={errors.description}
              multiline
              rows={6}
              size="small"
              fullWidth
            />

            {category === 'organization-delete' && (
              <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                {t('common:support.deletionAlert')}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ py: 1.5 }}>
          <Button
            onClick={() => setOpen(false)}
            color="inherit"
            disabled={submitting}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ textTransform: 'none', minWidth: 120 }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('common:support.submitCase')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>

    </>
  );
};

export default SupportCaseDialog;
